import { catchError, useSource } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { scheduleIterator } from "@opendash/schedule";
import dayjs from "dayjs";
import Parse from "parse";
import { useEffect, useState } from "react";
import { ScheduleState, useSourceWithChildren } from "..";
import { Schedule_Step, Schedule_Template } from "../../../js/parsegenerated";

/**
 * This hook should fetch the schedules for a single date.
 * It creates a cron state for every date passed and filters the schedules that fit the date by using cronIterator.
 *
 */
export const useScheduleForSingleDate = () => {
  const [scheduleStates, setScheduleStates] = useState<ScheduleState[]>([]);
  const [source] = useSource();
  const sourceIds = useSourceWithChildren(source.id);
  const [reload, setReload] = useState<boolean>(false);

  useEffect(() => {
    const fetchSchedules = async () => {
      const [schedulesError, schedules] = await catchError(
        new Parse.Query(Schedule_Template)
          .matchesQuery(
            "sources",
            new Parse.Query(Source).containedIn("objectId", sourceIds)
          )
          .find()
      );
      if (schedulesError) {
        return [];
      }
      const fetchedScheduleStates: Promise<ScheduleState>[] = schedules.map(
        async (schedule) => {
          //Fetch steps and sources from schedule relation
          const [stepsError, steps] = await catchError(
            schedule.relation("steps").query().includeAll().find()
          );

          const [sourcesError, sources] = await catchError(
            schedule.relation("sources").query().includeAll().find()
          );

          if (stepsError || sourcesError) {
            return { schedule, steps: [], sources: [] };
          }

          return {
            schedule,
            steps: steps as Schedule_Step[],
            sources: sources as Source[],
          };
        }
      );

      Promise.all(fetchedScheduleStates).then((resolvedScheduleData) =>
        setScheduleStates(resolvedScheduleData)
      );
    };

    fetchSchedules();
  }, [reload, sourceIds]);

  function getSchedulesOnDate(date: dayjs.Dayjs) {
    return scheduleStates.filter((scheduleState) =>
      scheduleIterator(scheduleState.schedule.get("cron"))?.matchesDate(
        date.toDate()
      )
    );
  }

  return {
    scheduleStates,
    getSchedulesOnDate,
    refetchSchedulesOnDate: () => setReload(!reload),
  };
};
